import React, { useEffect, useState, useContext } from 'react';
import Table from 'react-bootstrap/Table';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import Pagination from '@mui/material/Pagination';
import Tooltip from '@mui/material/Tooltip';
import { NavLink, useParams } from 'react-router-dom';
import { postRequest } from '../../../lib/api';
import { AuthContext } from '../../../authContext/context';
import toast from 'react-hot-toast';
import { formatDateTime } from '../../../utlis/dateandtime';
import Swal from 'sweetalert2';
import { usePermissions } from '../../../hooks/usePermissions';
import { Typography } from '@mui/material';

export default function Notes() {
  const { id } = useParams(); // customer_id
  const authCtx = useContext(AuthContext);
  const { hasPermission } = usePermissions();
  const [notes, setNotes] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  // Check permissions
  const canViewNotes = hasPermission('notes', 'view');
  const canEditNotes = hasPermission('notes', 'edit');
  const canCreateNotes = hasPermission('notes', 'create');

  const fetchNotes = async (pageVal = 1) => {
    try {
      const res = await postRequest(`private/notesList`, {
        customer_id: id,
        page: pageVal,
        limit: 8,
      }, authCtx.token);

      if (res?.status === 200 && res?.data?.docs) {
        setNotes(res.data.docs);
        setTotalPages(res.data.totalPages || 1);
      } else {
        toast.error(res?.message || 'Failed to fetch notes');
        setNotes([]);
        setTotalPages(1);
      }
    } catch (err) {
      //toast.error('Something went wrong while fetching notes');
    }
  };

  const handleDelete = async (noteId) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: 'Do you want to delete this note?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#bb0000',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, delete it!',
    });

    if (result.isConfirmed) {
      try {
        const res = await postRequest(`private/deleteNotes/${noteId}`, {}, authCtx.token);
        if (res?.status === 200) {
          toast.success(res.message || 'Note deleted successfully');
          fetchNotes(page); // Refresh list
        } else {
          toast.error(res?.message || 'Failed to delete note');
        }
      } catch (err) {
        toast.error('Something went wrong while deleting note');
      }
    }
  };

  useEffect(() => {
    if (id) fetchNotes(page);
  }, [id, page]);

  const handlePageChange = (e, value) => setPage(value);

  // If user doesn't have permission to view notes
  if (!canViewNotes) {
    return (
      <div className="mt-4">
        <Typography variant="h6" color="text.secondary" className="text-center">
          You do not have permission to view notes.
        </Typography>
      </div>
    );
  }

  return (
    <div className="mt-4">
      <Table striped responsive bordered>
        <thead>
          <tr>
            <th colSpan={6} className="bg-grey">Notes</th>
          </tr>
          <tr>
            <th>Name</th>
            <th>Date</th>
            <th>Notes</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {notes.length === 0 ? (
            <tr>
              <td colSpan={4} className="text-center">No notes found</td>
            </tr>
          ) : (
            notes.map((note, idx) => (
              <tr key={idx}>
                <td>{note?.username || '-'}</td>
                <td>{formatDateTime(note?.createdAt).date} {formatDateTime(note?.createdAt).time}</td>
                <td>{note?.note || '-'}</td>
                <td>
                  {canEditNotes && (
                    <>
                      <NavLink to={`/customer/edit/note/${note._id}`} className="red-link-text cursor-pointer w-18">
                        <Tooltip title="Edit"><EditOutlinedIcon /></Tooltip>
                      </NavLink>
                      &nbsp;&nbsp;&nbsp;
                    </>
                  )}
                  {canEditNotes && (
                    <span
                      className="red-link-text cursor-pointer w-18"
                      onClick={() => handleDelete(note._id)}
                    >
                      <Tooltip title="Delete"><DeleteOutlineOutlinedIcon /></Tooltip>
                    </span>
                  )}
                  {!canEditNotes && <span>-</span>}
                </td>
              </tr>
            ))
          )}
        </tbody>
      </Table>

      {notes.length > 0 && (
        <div style={{ display: 'flex', justifyContent: 'center', marginTop: 16 }}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={handlePageChange}
            color="primary"
            shape="rounded"
            size="medium"
            sx={{
                '& .Mui-selected': {
                  background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%) !important',
                  color: '#fff !important',
                },
                '& .MuiPaginationItem-root': {
                  color: 'var(--bs-red-color)',
                  borderColor: 'var(--bs-red-color)',
                },
              }}
          />
        </div>
      )}
    </div>
  );
}
